import { ClassOf, Entity, FactoryInterface } from './FactoryInterface';

import { UserFactory } from './factories/UserFactory';
import { JuridicalPersonFactory } from './factories/JuridicalPersonFactory';
import { PhysicalPersonFactory } from './factories/PhysicalPersonFactory';
import { ProjectFactory } from './factories/ProjectFactory';
import { CompanyFactory } from './factories/CompanyFactory';
import { InvestorTypeFactory } from './factories/InvestorTypeFactory';
import { AptitudeTestFactory } from './factories/AptitudeTestFactory';
import { AptitudeTestSectionFactory } from './factories/AptitudeTestSectionFactory';
import { AptitudeTestQuestionFactory } from './factories/AptitudeTestQuestionFactory';
import { AptitudeTestChoiceFactory } from './factories/AptitudeTestChoiceFactory';
import { FilledAptitudeTestFactory } from './factories/FilledAptitudeTestFactory';
import { FilledAptitudeTestQuestionFactory } from './factories/FilledAptitudeTestQuestionFactory';
import { FilledAptitudeTestSectionFactory } from './factories/FilledAptitudeTestSectionFactory';
import { MetricsFactory } from './factories/MetricsFactory';
import { ProjectCommentFactory } from './factories/ProjectCommentFactory';
import { ProjectChildCommentFactory } from './factories/ProjectChildCommentFactory';
import { InvestmentFactory } from './factories/InvestmentFactory';
import { SectorOfInterestFactory } from './factories/SectorOfInterestFactory';
import { PersonFactory } from './factories/PersonFactory';

import User from 'src/entities/User';
import JuridicalPerson from 'src/entities/JuridicalPerson';
import PhysicalPerson from 'src/entities/PhysicalPerson';
import Project from 'src/entities/Project';
import Company from 'src/entities/Company';
import InvestorType from 'src/entities/InvestorType';
import Test from 'src/entities/AptitudeTest';
import TestSection from 'src/entities/AptitudeTestSection';
import TestQuestion from 'src/entities/AptitudeTestQuestion';
import TestChoice from 'src/entities/AptitudeTestChoice';
import FilledTest from 'src/entities/FilledAptitudeTest';
import FilledAptitudeTestSection from 'src/entities/FilledAptitudeTestSection';
import FilledAptitudeTestQuestion from 'src/entities/FilledAptitudeTestQuestion';
import Metrics from 'src/entities/Metrics';
import ProjectComment from 'src/entities/ProjectComment';
import ProjectChildComment from 'src/entities/ProjectChildComment';
import Investment from 'src/entities/Investment';
import SectorOfInterest from 'src/entities/SectorOfInterest';
import InvestorDeck from 'src/entities/InvestorDeck';
import { ContractCorrectionsFactory } from 'src/services/entity-factory/factories/ContractCorrectionsFactory';
import ContractCorrections from 'src/value-objects/ContractCorrections';
import { ProjectTeamMemberFactory } from 'src/services/entity-factory/factories/ProjectTeamMemberFactory';
import ProjectTeamMember from 'src/value-objects/ProjectTeamMembers';
import { OfflineInvestorFactory } from 'src/services/entity-factory/factories/OfflineInvestorFactory';
import OfflineInvestor from 'src/entities/OfflineInvestor';
import { InvestorDeckFactory } from 'src/services/entity-factory/factories/InvestorDeckFactory';
import Person from 'src/entities/Person';
import { QuarterlyReportFactory } from 'src/services/entity-factory/factories/QuarterlyReportFactory';
import QuarterlyReport from 'src/entities/QuarterlyReport';
import { HomeBannerFactory } from 'src/services/entity-factory/factories/HomeBannerFactory';
import HomeBanner from 'src/entities/HomeBanner';
import { TestimonialFactory } from 'src/services/entity-factory/factories/TestimonialFactory';
import Testimonial from 'src/entities/Testimonial';

export class EntityFactoryFactory {
  private static selfReference: EntityFactoryFactory;

  public readonly userFactory: UserFactory;
  public readonly juridicalPersonFactory: JuridicalPersonFactory;
  public readonly physicalPersonFactory: PhysicalPersonFactory;
  public readonly investorTypeFactory: InvestorTypeFactory;
  public readonly projectFactory: ProjectFactory;
  public readonly companyFactory: CompanyFactory;
  public readonly aptitudeTestFactory: AptitudeTestFactory;
  public readonly aptitudeTestSectionFactory: AptitudeTestSectionFactory;
  public readonly aptitudeTestQuestionFactory: AptitudeTestQuestionFactory;
  public readonly aptitudeTestChoiceFactory: AptitudeTestChoiceFactory;
  public readonly filledAptitudeTestFactory: FilledAptitudeTestFactory;
  public readonly filledAptitudeTestSectionFactory: FilledAptitudeTestSectionFactory;
  public readonly filledAptitudeTestQuestionFactory: FilledAptitudeTestQuestionFactory;
  public readonly metricsFactory: MetricsFactory;
  public readonly projectCommentFactory: ProjectCommentFactory;
  public readonly projectChildCommentFactory: ProjectChildCommentFactory;
  public readonly investmentFactory: InvestmentFactory;
  public readonly sectorOfInterestFactory: SectorOfInterestFactory;
  public readonly personFactory: PersonFactory;
  public readonly contractCorrectionsFactory: ContractCorrectionsFactory;
  public readonly projectTeamMemberFactory: ProjectTeamMemberFactory;
  public readonly offlineInvestorFactory: OfflineInvestorFactory;
  public readonly investorDeckFactory: InvestorDeckFactory;
  public readonly quarterlyReportFactory: QuarterlyReportFactory;
  public readonly homeBannerFactory: HomeBannerFactory;
  public readonly testimonialFactory: TestimonialFactory;

  constructor() {
    if (EntityFactoryFactory.selfReference) {
      return EntityFactoryFactory.selfReference;
    }

    EntityFactoryFactory.selfReference = this;

    this.userFactory = new UserFactory(this);
    this.juridicalPersonFactory = new JuridicalPersonFactory(this);
    this.physicalPersonFactory = new PhysicalPersonFactory(this);
    this.investorTypeFactory = new InvestorTypeFactory(this);
    this.projectFactory = new ProjectFactory(this);
    this.companyFactory = new CompanyFactory(this);
    this.aptitudeTestFactory = new AptitudeTestFactory(this);
    this.aptitudeTestSectionFactory = new AptitudeTestSectionFactory(this);
    this.aptitudeTestQuestionFactory = new AptitudeTestQuestionFactory(this);
    this.aptitudeTestChoiceFactory = new AptitudeTestChoiceFactory(this);
    this.filledAptitudeTestFactory = new FilledAptitudeTestFactory(this);
    this.filledAptitudeTestSectionFactory = new FilledAptitudeTestSectionFactory(this);
    this.filledAptitudeTestQuestionFactory = new FilledAptitudeTestQuestionFactory(this);
    this.metricsFactory = new MetricsFactory(this);
    this.projectCommentFactory = new ProjectCommentFactory(this);
    this.projectChildCommentFactory = new ProjectChildCommentFactory(this);
    this.investmentFactory = new InvestmentFactory(this);
    this.sectorOfInterestFactory = new SectorOfInterestFactory(this);
    this.personFactory = new PersonFactory(this);
    this.contractCorrectionsFactory = new ContractCorrectionsFactory(this);
    this.projectTeamMemberFactory = new ProjectTeamMemberFactory(this);
    this.offlineInvestorFactory = new OfflineInvestorFactory(this);
    this.investorDeckFactory = new InvestorDeckFactory(this);
    this.quarterlyReportFactory = new QuarterlyReportFactory(this);
    this.homeBannerFactory = new HomeBannerFactory(this);
    this.testimonialFactory = new TestimonialFactory(this);
  }

  public getFactory<T extends Entity>(entityType: ClassOf<T>): FactoryInterface<T> {
    switch (entityType.prototype) {
      case User.prototype:
        return this.userFactory as FactoryInterface<T>;
      case JuridicalPerson.prototype:
        return this.juridicalPersonFactory as FactoryInterface<T>;
      case PhysicalPerson.prototype:
        return this.physicalPersonFactory as FactoryInterface<T>;
      case InvestorType.prototype:
        return this.investorTypeFactory as FactoryInterface<T>;
      case Project.prototype:
        return this.projectFactory as FactoryInterface<T>;
      case Company.prototype:
        return this.companyFactory as FactoryInterface<T>;
      case Test.prototype:
        return this.aptitudeTestFactory as FactoryInterface<T>;
      case TestSection.prototype:
        return this.aptitudeTestSectionFactory as FactoryInterface<T>;
      case TestQuestion.prototype:
        return this.aptitudeTestQuestionFactory as FactoryInterface<T>;
      case TestChoice.prototype:
        return this.aptitudeTestChoiceFactory as FactoryInterface<T>;
      case FilledTest.prototype:
        return this.filledAptitudeTestFactory as FactoryInterface<T>;
      case FilledAptitudeTestSection.prototype:
        return this.filledAptitudeTestSectionFactory as FactoryInterface<T>;
      case FilledAptitudeTestQuestion.prototype:
        return this.filledAptitudeTestQuestionFactory as FactoryInterface<T>;
      case Metrics.prototype:
        return this.metricsFactory as FactoryInterface<T>;
      case ProjectComment.prototype:
        return this.projectCommentFactory as FactoryInterface<T>;
      case ProjectChildComment.prototype:
        return this.projectChildCommentFactory as FactoryInterface<T>;
      case Investment.prototype:
        return this.investmentFactory as FactoryInterface<T>;
      case SectorOfInterest.prototype:
        return this.sectorOfInterestFactory as FactoryInterface<T>;
      case Person.prototype:
        return this.personFactory as FactoryInterface<T>;
      case ContractCorrections.prototype:
        return this.contractCorrectionsFactory as FactoryInterface<T>;
      case ProjectTeamMember.prototype:
        return this.projectTeamMemberFactory as FactoryInterface<T>;
      case OfflineInvestor.prototype:
        return this.offlineInvestorFactory as FactoryInterface<T>;
      case InvestorDeck.prototype:
        return this.investorDeckFactory as FactoryInterface<T>;
      case QuarterlyReport.prototype:
        return this.quarterlyReportFactory as FactoryInterface<T>;
      case HomeBanner.prototype:
        return this.homeBannerFactory as FactoryInterface<T>;
      case Testimonial.prototype:
        return this.testimonialFactory as FactoryInterface<T>;
      default:
        throw 'Unknown factory!';
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/entity-factory/EntityFactoryFactory.tsx